import loadable from '@loadable/component';
import ErrorBoundary from 'components/ErrorBoundary';
import React, {
  ComponentType,
  Key,
  ReactElement,
  ReactNode,
  useEffect,
} from 'react';
import type { RouteObject } from 'react-router-dom';
import { Route } from 'react-router-dom';
import { isCordova } from 'utils/platform';
import useBreakPoint from '~/hooks/useBreakPoint';
import { toggleMessenger } from '~/trackers/tools/zendesk/handler';

const LoadableNavbar = loadable(
  () =>
    import(
      /* webpackChunkName: "navbar" */ '~/reusable-components/navigation/Navbar'
    ),
  {},
  true,
);
const LoadableTabBar = loadable(
  () =>
    import(
      /* webpackChunkName: "tabbar" */ '~/reusable-components/navigation/TabBar'
    ),
  {},
  true,
);
const LoadableFooter = loadable(
  () => import(/* webpackChunkName: "footer" */ 'modules/common/Footer'),
  {},
  true,
);

export type NavigationalLayoutProps = {
  overlay?: ReactNode;
  transparentNavbar?: boolean;
  nonFixedNavbar?: boolean;
  component: ComponentType<any>;
  onTopOf?: ComponentType<any>;
  showSearchInNavbar?: boolean;
  showFullSearchInNavbar?: boolean;
  hideFooter?: boolean;
  showBottomNavigation?: boolean;
  subNavbar?: ReactElement;
  subNavbarMobile?: ReactElement;
  bottomBar?: ReactElement;
  noNavbar?: boolean;
  showZendesk?: boolean;
};

export const NavigationalLayoutElement = ({
  component,
  onTopOf,
  overlay,
  showSearchInNavbar = true,
  showFullSearchInNavbar = false,
  hideFooter = false,
  noNavbar,
  transparentNavbar = false,
  nonFixedNavbar,
  showBottomNavigation,
  subNavbar,
  subNavbarMobile,
  bottomBar,
  showZendesk = true,
}: NavigationalLayoutProps) => {
  const breakpoint = useBreakPoint();
  const isMobile = breakpoint === 'xs';

  useEffect(() => {
    toggleMessenger(showZendesk && !isMobile);
  }, [showZendesk, isMobile]);

  const SubComponent = component;
  const UnderlyingPage = onTopOf ?? null;

  return (
    <>
      {!noNavbar && (
        <LoadableNavbar
          fixed={nonFixedNavbar ? 'none' : 'all'}
          variant={transparentNavbar ? 'transparent' : 'default'}
          showSearch={showSearchInNavbar}
          showFullSearch={showFullSearchInNavbar}
          subNavbar={subNavbar}
          subNavbarMobile={subNavbarMobile}
        />
      )}
      <ErrorBoundary>
        {onTopOf && <UnderlyingPage />}
        <SubComponent />
      </ErrorBoundary>
      {overlay}
      {hideFooter ? null : <LoadableFooter />}
      {showBottomNavigation && isCordova() && (
        <LoadableTabBar bottomBar={bottomBar} />
      )}
    </>
  );
};

type NavigationalRouteOptions = NavigationalLayoutProps & {
  path?: string;
  index?: boolean;
  key?: Key;
  caseSensitive?: boolean;
  handle?: RouteObject['handle'];
  id?: string;
  loader?: RouteObject['loader'];
  action?: RouteObject['action'];
  errorElement?: RouteObject['errorElement'];
  shouldRevalidate?: RouteObject['shouldRevalidate'];
  hasErrorBoundary?: RouteObject['hasErrorBoundary'];
};

export const createNavigationalRoute = ({
  component,
  path,
  index,
  caseSensitive,
  key,
  handle,
  id,
  loader,
  action,
  errorElement,
  shouldRevalidate,
  hasErrorBoundary,
  ...props
}: NavigationalRouteOptions) => {
  const routeProps = index ? { index: true as const } : { path, caseSensitive };

  return (
    <Route
      key={key}
      {...routeProps}
      handle={handle}
      id={id}
      loader={loader}
      action={action}
      errorElement={errorElement}
      shouldRevalidate={shouldRevalidate}
      hasErrorBoundary={hasErrorBoundary}
      element={<NavigationalLayoutElement {...props} component={component} />}
    />
  );
};
